import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Label} from "../drawer/drawer/Style";
import SearchSelectorPanel from "../searchSelector/SearchSelectorPanel";
import useCodeData from "../../hooks/useCodeData";
import {useClickOutside} from "../../hooks/useClickOutside";
import {
    applySearchDrawerDropdownSizing,
    computeDropdownFixedPosition,
    getDropdownPortalStyle,
} from "../../utils/selectbox/dropdownViewportPosition";
import {
    findOptionById,
    getOptionName,
    getParentChainFromTree,
    getBreadcrumbDisplayChain,
    hasExtendedPanel,
    toArray,
} from "../../utils/selectbox/selectorUtils";
import {useInSearchDetail} from "../search/SearchDetailContext";
import {parseSearchSelectorFilters} from "../searchSelector/parseSearchSelectorFilters";
import {ChevronDownIcon} from "./SelectorIcons";
import CommonPagination from "../table/movable/CommonPagination";
import CodeCreateModal from "./CodeCreateModal";
import SelectorTreeList from "./SelectorTreeList";
import SelectorOptionLabel from "./SelectorOptionLabel";
import "./selector.css";
import "./codeCreateModal.css";

const DROPDOWN_WIDTH = 235;
const EXTENDED_PANEL_WIDTH = 560;
const EXTENDED_PANEL_COMPACT_HINT = 220;
const EXTENDED_PANEL_WIDE_HINT = 360;
const ESTIMATED_DROPDOWN_HEIGHT = 360;
const ESTIMATED_EXTENDED_HEIGHT = 520;

function Selector({
    onChange,
    data,
    title,
    entity,
    parentKey,
    constraint,
    auth,
    recursive,
    placeHolder,
    uniqueKey,
    children,
    showLabel,
    showBreadcrumb = true,
    notInsert,
    useMultiSearch,
    updater,
    readOnly,
    /** `document.body` 포털 z-index. JsExcelGrid fullmode(9999) 위에 띄울 때 10050 등 지정. */
    dropdownZIndex,
    /** id가 없을 때 name으로 active 매칭(엑셀 등 특수 케이스) */
    matchActiveByNameWhenIdMissing = false,
    /** 드롭다운 내 "선택 취소" 버튼 표시(엑셀 등) */
    showClearAction = false,
    /** 트리거 버튼 테마 — `ios` 사파리 스타일(기본), `basic` flat 스타일 */
    theme = "ios",
}) {
    const isIosTheme = theme === "ios";
    const inSearchDetail = useInSearchDetail();
    const enableMultiSearch = useMultiSearch && !inSearchDetail;
    const editable = !!auth && !readOnly;
    const emptyLabel = placeHolder ?? "선택";
    const extendedPanel = hasExtendedPanel(children, enableMultiSearch);
    const extendedPanelWide = parseSearchSelectorFilters(children).length > 0;

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [expandedMenus, setExpandedMenus] = useState({});
    const [panelLabel, setPanelLabel] = useState("");
    const [dropdownPos, setDropdownPos] = useState({top: 0, left: 0});
    const [codeModalMode, setCodeModalMode] = useState(null);
    const [createParentId, setCreateParentId] = useState(null);
    /** 자체 dropdown(extendedPanel=false) 내부 검색어 */
    const [searchText, setSearchText] = useState("");

    const rootCodeId = parentKey ?? 1;

    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const pendingClearRef = useRef(false);

    const {dataSet, loading, pageInfo, handlePageChange, refetch} = useCodeData(entity, uniqueKey);

    const isRequiredEmpty = constraint?.required && !data?.id && !getOptionName(data);

    const treeCodes = useMemo(() => {
        if (!dataSet) return [];
        const content = Array.isArray(dataSet) ? dataSet : dataSet.content;
        if (!content?.length) return [];
        if (parentKey) {
            return content.filter((each) => each.id === parentKey);
        }
        if (recursive) {
            return [{id: "root", children: content}];
        }
        return content;
    }, [dataSet, parentKey, recursive]);

    const root = useMemo(() => (treeCodes.length ? treeCodes[0] : null), [treeCodes]);

    const treeListRoots = useMemo(() => {
        if (!recursive) return treeCodes;
        return root ? toArray(root.children) : [];
    }, [recursive, root, treeCodes]);

    const parentChain = useMemo(() => {
        if (!recursive || !selected || !treeCodes.length) return [];
        return getParentChainFromTree(treeCodes, selected);
    }, [recursive, selected, treeCodes]);

    const breadcrumbChain = useMemo(
        () => getBreadcrumbDisplayChain(parentChain),
        [parentChain],
    );

    const closePanel = useCallback(() => setOpen(false), []);

    useClickOutside([dropdownRef, triggerRef, rootRef], closePanel, open);

    useEffect(() => {
        if (!data?.id) {
            pendingClearRef.current = false;
            setSelected(null);
            return;
        }
        if (pendingClearRef.current) {
            return;
        }
        if (recursive && root) {
            setSelected(findOptionById([root], data.id) ?? data);
            return;
        }
        if (treeCodes.length) {
            setSelected(findOptionById(treeCodes, data.id) ?? data);
            return;
        }
        setSelected(data);
    }, [data, treeCodes, root, recursive]);

    useEffect(() => {
        if (!open || !recursive || !selected || !treeCodes.length) return;
        const chain = getParentChainFromTree(treeCodes, selected);
        setExpandedMenus((prev) => {
            const next = {...prev};
            chain.forEach((item) => {
                next[item.id] = true;
            });
            return next;
        });
    }, [open, recursive, selected, treeCodes]);

    useLayoutEffect(() => {
        if (!open) return;

        const updatePosition = () => {
            const triggerEl = triggerRef.current;
            if (!triggerEl) return;
            const pos = computeDropdownFixedPosition({
                triggerEl,
                dropdownEl: dropdownRef.current,
                dropdownMinWidth: extendedPanel
                    ? (extendedPanelWide ? EXTENDED_PANEL_WIDE_HINT : EXTENDED_PANEL_COMPACT_HINT)
                    : DROPDOWN_WIDTH,
                estimatedHeight: extendedPanel ? ESTIMATED_EXTENDED_HEIGHT : ESTIMATED_DROPDOWN_HEIGHT,
                contentSized: extendedPanel,
            });
            applySearchDrawerDropdownSizing(dropdownRef.current, triggerEl, pos, {
                widePanel: extendedPanelWide,
            });
            setDropdownPos(pos);
        };

        updatePosition();
        const raf = requestAnimationFrame(updatePosition);
        const ro = new ResizeObserver(updatePosition);
        if (dropdownRef.current) ro.observe(dropdownRef.current);

        const onWin = () => updatePosition();
        window.addEventListener("resize", onWin);
        window.addEventListener("scroll", onWin, true);
        window.visualViewport?.addEventListener("resize", onWin);
        window.visualViewport?.addEventListener("scroll", onWin);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin, true);
            window.visualViewport?.removeEventListener("resize", onWin);
            window.visualViewport?.removeEventListener("scroll", onWin);
        };
    }, [open, treeListRoots, loading, extendedPanel, expandedMenus]);

    const tryToggle = (e) => {
        e?.stopPropagation?.();
        if (readOnly) return;
        if (!editable) {
            alert("권한이 없습니다.");
            return;
        }
        setOpen((prev) => !prev);
    };

    const handleSelect = (option) => {
        if (!option || !editable) return;
        onChange(option);
        setSelected(option);
        setOpen(false);
    };

    const handleToggleExpand = (id) => {
        setExpandedMenus((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (!editable) return;
        pendingClearRef.current = true;
        onChange(null);
        setSelected(null);
        setOpen(false);
    };

    const handleCreateCode = () => {
        setCreateParentId(rootCodeId);
        setCodeModalMode("create");
        setOpen(false);
    };

    const handleUpdateCode = () => {
        setCreateParentId(rootCodeId);
        setCodeModalMode("update");
        setOpen(false);
    };

    const closeCodeModal = () => setCodeModalMode(null);

    const handleCodeModalSuccess = async () => {
        await refetch?.();
    };

    const modalCategories = useMemo(() => {
        if (!treeCodes.length) return [];
        if (recursive && root) {
            return [root];
        }
        return treeCodes;
    }, [treeCodes, root, recursive]);

    const triggerText = selected ? getOptionName(selected) : emptyLabel;
    const readonlyDisplayName =
        (selected ? getOptionName(selected) : "") ||
        (data ? getOptionName(data) : "") ||
        "";

    /** 자체 dropdown 검색용 — 트리/리스트를 평면화 */
    const flatSearchNodes = useMemo(() => {
        const result = [];
        const walk = (nodes) => {
            if (!Array.isArray(nodes)) return;
            nodes.forEach((node) => {
                if (!node) return;
                result.push(node);
                walk(node.children);
            });
        };
        walk(treeListRoots);
        return result;
    }, [treeListRoots]);

    const trimmedSearch = (searchText ?? "").trim().toLowerCase();
    const isSearching = trimmedSearch !== "";
    const filteredSearchResults = useMemo(() => {
        if (!isSearching) return [];
        return flatSearchNodes.filter((node) => {
            const name = (getOptionName(node) ?? "").toLowerCase();
            return name.includes(trimmedSearch);
        });
    }, [flatSearchNodes, isSearching, trimmedSearch]);

    useEffect(() => {
        if (!open) setSearchText("");
    }, [open]);

    const renderDropdown = () => {
        if (extendedPanel) {
            return (
                <SearchSelectorPanel
                    open={open}
                    onClose={closePanel}
                    useMultiSearch={enableMultiSearch}
                    data={data}
                    onChange={onChange}
                    recursive={recursive}
                    label={panelLabel}
                    constraint={constraint}
                    placeHolder={placeHolder}
                    setLabel={setPanelLabel}
                    children={children}
                    title={title}
                    entity={entity}
                    uniqueKey={uniqueKey}
                    parentKey={parentKey}
                    showLabel={showLabel}
                    notInsert={notInsert}
                />
            );
        }

        const renderFlatOption = (option) => {
            const selectedById = String(selected?.id) === String(option.id);
            const selectedByName =
                matchActiveByNameWhenIdMissing
                && (selected?.id == null)
                && String(getOptionName(selected) ?? "").trim() !== ""
                && String(getOptionName(selected) ?? "").trim()
                    === String(getOptionName(option) ?? "").trim();
            const isSelected = selectedById || selectedByName;
            return (
                <div
                    key={option.id}
                    className={`selector-option ${isSelected ? "is-selected" : ""}`}
                >
                    <div className="selector-option-select" onClick={() => handleSelect(option)}>
                        <SelectorOptionLabel option={option} />
                    </div>
                </div>
            );
        };

        return (
            <div className="selector-dropdown">
                <div className="selector-dropdown-search-wrap">
                    <input
                        type="text"
                        autoComplete="off"
                        className="selector-dropdown-search"
                        placeholder="검색하세요."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>

                {showClearAction && selected && (
                    <div style={{padding: "0 10px 8px"}}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!editable) return;
                                onChange(null);
                                setSelected(null);
                                setOpen(false);
                            }}
                            style={{
                                width: "100%",
                                height: 30,
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                fontSize: 12,
                                color: "#374151",
                            }}
                        >
                            선택 취소
                        </button>
                    </div>
                )}

                <div className="selector-dropdown-list selector-dropdown-list--tree">
                    {loading ? (
                        <div className="selector-loading">불러오는 중...</div>
                    ) : isSearching ? (
                        filteredSearchResults.length > 0 ? (
                            filteredSearchResults.map((option) => renderFlatOption(option))
                        ) : (
                            <div className="selector-empty">검색 결과 없음</div>
                        )
                    ) : treeListRoots.length > 0 ? (
                        recursive ? (
                            <SelectorTreeList
                                list={treeListRoots}
                                expandedMenus={expandedMenus}
                                onToggleExpand={handleToggleExpand}
                                selected={selected}
                                onSelect={handleSelect}
                                recursive={recursive}
                                matchActiveByNameWhenIdMissing={matchActiveByNameWhenIdMissing}
                            />
                        ) : (
                            treeListRoots.map((option) => renderFlatOption(option))
                        )
                    ) : (
                        <div className="selector-empty">데이터 없음</div>
                    )}
                </div>

                {!loading && pageInfo?.totalPages > 0 && (
                    <CommonPagination page={pageInfo} onPageChange={handlePageChange}/>
                )}

                {updater && editable && (
                    <div className="selector-dropdown-footer">
                        {updater.onCreate && (
                            <button
                                type="button"
                                className="selector-dropdown-footer-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateCode();
                                }}
                            >
                                + 코드 만들기
                            </button>
                        )}
                        {updater.onUpdate && (
                            <button
                                type="button"
                                className="selector-dropdown-footer-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateCode();
                                }}
                            >
                                + 코드 수정하기
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    /** 보기 모드(auth 없음): KV 테이블과 동일한 읽기 전용 텍스트 */
    if (!editable) {
        const readonlyValue = readonlyDisplayName || "\u00A0";
        if (showLabel) {
            return (
                <div className={`simms-drawer-field selector-field ${showLabel ? "selector-field--inline" : ""}`}>
                    <Label>{title}</Label>
                    <span className="asset-detail-kv-value">{readonlyValue}</span>
                </div>
            );
        }
        return <span className="asset-detail-kv-value selector-readonly-value">{readonlyValue}</span>;
    }

    return (
        <div className={`simms-drawer-field selector-field ${showLabel ? "selector-field--inline" : ""}`}>
            {showLabel && <Label>{title}</Label>}

            <div ref={rootRef} className={`selector-root no-drag${isIosTheme ? " selector-root--ios" : ""}`}>
                <div
                    ref={triggerRef}
                    className={`selector-trigger simms-drawer-trigger ${isRequiredEmpty ? "is-required-empty" : ""} ${selected ? "is-selected" : ""} ${open ? "is-open" : ""}`}
                    onClick={tryToggle}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            tryToggle(e);
                        }
                    }}
                >
                    <span className={`selector-trigger-label ${selected ? "is-selected" : ""}`}>
                        {triggerText}
                    </span>
                    <ChevronDownIcon className={`selector-trigger-chevron ${open ? "is-open" : ""}`} />
                </div>

                {showBreadcrumb && selected && !extendedPanel && (
                    <div className="selector-breadcrumb">
                        <div className="selector-breadcrumb-inner">
                            <span className="selector-breadcrumb-text">
                                {"| "}
                                {recursive && breadcrumbChain.map((item, idx) => (
                                    <span key={item.id}>
                                        {getOptionName(item)}
                                        {idx < breadcrumbChain.length - 1 ? " > " : ""}
                                    </span>
                                ))}
                                {recursive && breadcrumbChain.length > 0 && " > "}
                                <span className="selector-breadcrumb-current">{getOptionName(selected)}</span>
                            </span>
                            {editable && (
                                <button type="button" className="selector-breadcrumb-clear" onClick={handleClear} title="초기화">
                                    –
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {open && typeof document !== "undefined" && createPortal(
                    <div
                        ref={dropdownRef}
                        className={`fixed ${dropdownZIndex == null ? "z-[1100]" : ""} ${extendedPanel ? "search-selector-dropdown " : ""}${dropdownPos.inSearchDrawer ? "search-detail-drawer-dropdown" : ""}`}
                        style={{
                            ...getDropdownPortalStyle(dropdownPos, {
                                extendedPanel: extendedPanelWide,
                                defaultWidth: DROPDOWN_WIDTH,
                                extendedWidth: EXTENDED_PANEL_WIDTH,
                            }),
                            ...(dropdownZIndex != null ? {zIndex: dropdownZIndex} : {}),
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => {
                            if (dropdownZIndex != null) e.stopPropagation();
                        }}
                    >
                        {renderDropdown()}
                    </div>,
                    document.body,
                )}

                <CodeCreateModal
                    open={!!codeModalMode}
                    onClose={closeCodeModal}
                    title={codeModalMode === "update" ? "코드 수정하기" : "코드 만들기"}
                    mode={codeModalMode ?? "create"}
                    value={createParentId}
                    rootParentId={rootCodeId}
                    categories={modalCategories}
                    recursive={recursive}
                    onCreate={updater?.onCreate}
                    onUpdate={updater?.onUpdate}
                    onSuccess={handleCodeModalSuccess}
                />
            </div>
        </div>
    );
}

export default Selector;
